/* eslint-disable no-console */
import { Component, Input, forwardRef, Inject, HostListener } from '@angular/core';
import { FormControlDropdown } from '../../controls';
import { ModalController } from '@ionic/angular';
import { PlatformName, PlatformProvider, ReferenceTableModel } from '@shared';
import { SearcheableSelectModel, SearcheableSelectComponent } from '../searcheable-select';
import { Subscription } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { DropdownDataSourceProvider } from '../../services';
import { DOCUMENT } from '@angular/common';

@Component({
	selector: 'app-search-select-input',
	templateUrl: './searcheable-select-input.component.html',
	styleUrls: ['./searcheable-select-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SearcheableSelectInputComponent),
			multi: true
		}
	]
})
export class SearcheableSelectInputComponent implements ControlValueAccessor {
	@Input() public dynamicFormControl: FormControlDropdown;
	@Input() public form: UntypedFormGroup;

	public selectedValue: string | null;
	private refTableSubscription: Subscription;

	public constructor(
		private modalCtrl: ModalController,
		private dataSource: DropdownDataSourceProvider,
		private platformProvider: PlatformProvider,
		@Inject(DOCUMENT) private document: Document
	) {}

	@HostListener('window:resize', ['$event'])
	public getScreenSize(): number {
		return window.innerHeight;
	}

	public writeValue(value: string): void {
		this.selectedValue = value;
	}
	public registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}
	/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
	public registerOnTouched(fn: any): void {}
	/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
	public setDisabledState?(isDisabled: boolean): void {}

	public onOpenSelect(): void {
		if (this.dynamicFormControl.disabled) {
			return;
		}

		if (this.refTableSubscription) {
			this.refTableSubscription.unsubscribe();
		}

		this.refTableSubscription = this.dataSource
			.getReferenceTables(this.dynamicFormControl.refTableName)
			.subscribe(async (refTables: ReferenceTableModel[]) => {
				const selectOptions = refTables.map(
					(item: ReferenceTableModel) =>
						new SearcheableSelectModel({
							id: item[this.dynamicFormControl.refTableColumnValue.toLowerCase()],
							value: item[this.dynamicFormControl.refTableColumnValue.toLowerCase()],
							description: item[this.dynamicFormControl.refTableColumnDescription.toLowerCase()],
							displayBoth: true
						})
				);

				const maxBreakpoint = this.getMaxBreakpoint();

				const modal = await this.modalCtrl.create({
					component: SearcheableSelectComponent,
					cssClass: 'custom-searcheable-select',
					backdropDismiss: true,
					// breakpoints: [0, 0.5, maxBreakpoint],
					initialBreakpoint: maxBreakpoint,
					componentProps: {
						selectOptions,
						selectedItemId: this.selectedValue,
						controlLabel: this.dynamicFormControl.label
					},
					showBackdrop: false
				});
				await modal.present();
				const { data } = await modal.onWillDismiss();
				this.form.get(this.dynamicFormControl.key).markAsDirty();

				if (data) {
					if (data.displayBoth) {
						this.dynamicFormControl.selectedOption = {
							description: data.description,
							key: data.id,
							value: data.value
						};
						this.selectedValue = data.description;
						this.propagateChange(data.value);
					} else {
					
					this.dynamicFormControl.selectedOption = {
						description: data.description,
						key: data.id,
						value: data.value
					};
					this.selectedValue = data.value;
					this.propagateChange(data.value);
				}}

				
			});
	}

	/* eslint-disable @typescript-eslint/no-unused-vars */
	private propagateChange = (_: any) => {};

	// this method retrieves the max breakpoint where the dropdown can stretch to when open
	// first step is to read the height of the header element and assign a default value of 64px (current header height set),
	// in case the reading fails.
	// Based on the platform the app is running, a notch height is assignedȘ 48 for ios, 0 for others. However, there are ios device
	// which don't have notches, meaning that if the docHeaderHeight > 64 (has extra layer added - notch), then headerArea will
	// be the result of extracting notch from height of header element.
	// Last but not least, the returned result will be a value from 0 - 1, based on a calculation (+0.01 is just a fine tuning)
	private getMaxBreakpoint(): number {
		const docHeaderHeight = this.document.getElementById('app-header')?.clientHeight;

		const headerHeight = docHeaderHeight ? docHeaderHeight : 64;
		const notchHeight: number = this.platformProvider.getPlatform() === PlatformName.IOS ? 48 : 0;
		const headerArea = docHeaderHeight > 64 ? headerHeight - notchHeight : headerHeight;

		return 1 - headerArea / (this.getScreenSize() / 100) / 100 + 0.01;
	}
}
